import { Stack, Typography } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { useReportError } from '@/hooks/useReportError';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getNodes } from '@/utils/graphqlUtils';

import {
  AdminAiPromptsNavigation,
  AI_PROMPTS_DESCRIPTION,
} from '../AdminFormsPage/AdminAiPromptsNavigation';
import {
  mapPromptsToRows,
  Row,
  useSummaryPromptColumns,
} from './AdminSummaryPromptsPage.utils';
import { CreateCustomSummaryPromptModal } from './CreateCustomSummaryPromptModal';
import { useAdminSummaryPromptsPageQuery } from './graphql/AdminSummaryPromptsPage.generated';

export function AdminSummaryPromptsPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { showFeedback } = useFeedback();
  const { navigate } = useNavigateToRoute();
  const { data, loading, error } = useAdminSummaryPromptsPageQuery({
    fetchPolicy: 'cache-and-network',
    onError: () => {
      showFeedback(
        "We weren't able to load summary prompts. Please refresh the page and try again."
      );
    },
  });

  useReportError('failed to load data for summary prompts page', error);

  const columns = useSummaryPromptColumns();

  const prompts = useMemo(
    () => getNodes(data?.documentSummaryPrompts),
    [data?.documentSummaryPrompts]
  );

  const customPrompts = useMemo(
    () => prompts.filter((p) => !p.isSystemPrompt),
    [prompts]
  );

  const systemPrompts = useMemo(
    () => prompts.filter((p) => p.isSystemPrompt),
    [prompts]
  );

  const customRows = useMemo(
    () => mapPromptsToRows(customPrompts),
    [customPrompts]
  );

  const systemRows = useMemo(
    () => mapPromptsToRows(systemPrompts),
    [systemPrompts]
  );

  const handleRowClick = (row: GridRowParams<Row>) => {
    navigate(ROUTE_KEYS.ADMIN_PROMPT_ITERATION, {
      id: row.row.id,
    });
  };

  return (
    <SubpageLayout
      heading="AI prompts"
      description={AI_PROMPTS_DESCRIPTION}
      headingSpacing={3}
      actions={
        <Button
          size="sm"
          variant="secondary"
          startIcon={PlusIcon}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Custom prompt
        </Button>
      }
    >
      <Stack spacing={2}>
        <AdminAiPromptsNavigation />
        <DataTableCard>
          <Stack spacing={3}>
            {customRows.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="title-s">Custom prompts</Typography>
                <DataTable
                  columns={columns}
                  rows={customRows}
                  loading={loading}
                  alwaysShowPaginationFooter
                  pagination={false}
                  onRowClick={handleRowClick}
                  slots={{
                    noRowsOverlay: () => (
                      <TableEmptyState text="No custom prompts" />
                    ),
                  }}
                />
              </Stack>
            )}
            <Stack spacing={1}>
              <Typography variant="title-s">System prompts</Typography>
              <DataTable
                columns={columns}
                rows={systemRows}
                loading={loading}
                alwaysShowPaginationFooter
                pagination={false}
                onRowClick={handleRowClick}
                slots={{
                  noRowsOverlay: () => (
                    <TableEmptyState text="No system prompts" />
                  ),
                }}
              />
            </Stack>
          </Stack>
        </DataTableCard>
      </Stack>

      {isCreateModalOpen && (
        <CreateCustomSummaryPromptModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={(id) => {
            setIsCreateModalOpen(false);
            navigate(ROUTE_KEYS.ADMIN_PROMPT_ITERATION, { id });
          }}
        />
      )}
    </SubpageLayout>
  );
}
